import { FormEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import { format, isValid, isBefore, isAfter } from 'date-fns';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';
import { MEMBER_INFO_PATH } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { MemberBigNInput } from '@pass-frontend/data-access/common/edge';
import { env } from '@pass-frontend/features/configuration/util';
import { useMemberEligibility } from '@pass-frontend/features/facility-checkin/data-bridge';
import { RdsButton } from '@pass-frontend/rally-energon-react';
import { RESPONSIVE_QUERY } from '@pass-frontend/utility/style-helpers';
import { ErrorInfo } from '../error-info';

/* eslint-disable-next-line */
export interface EligibilityLookupProps {}

const StyledEligibilityLookup = styled.div``;

const PartnerButton = styled(RdsButton)`
  width: 130px;

  ${RESPONSIVE_QUERY.medium} {
    width: 100%;
  }
`;

const PartnerInput = styled.input`
  width: 100%;
  height: 40px;
`;

const AdditionalFields = styled.div`
  &.hide {
    display: none;
  }

  &.show {
    display: block;
  }
`;

const GroupIdField = styled.div`
  &.hide {
    display: none;
  }

  &.show {
    display: block;
  }
`;

const SecondaryLabel = styled.span`
  display: block;
`;

interface FieldErrors {
  date?: string;
  searchId?: string;
  zipCode?: string;
  groupId?: string;
}

export function EligibilityLookup(props: EligibilityLookupProps) {
  const [memberEligibilityError, setMemberEligibilityError] = useState(false);
  const { ENABLE_FACILITY_CODE_CHECK } = env();
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showGroupId, setShowGroupId] = useState(false);

  const [eligibilityForm, setEligibilityForm] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    searchId: '',
    zipCode: '',
    groupId: '',
  });

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const setFieldError = (name: keyof FieldErrors, value?: string) =>
    setFieldErrors((errors) => ({ ...errors, [name]: value }));

  const { error, memberCheckForBigN, fetchMemberByEligiblity } =
    useMemberEligibility();
  const router = useRouter();

  const { t } = useIntl();

  const setFormValue = (field: string, value: string) => {
    setEligibilityForm((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const hasData = () => {
    return (
      eligibilityForm.firstName ||
      eligibilityForm.lastName ||
      eligibilityForm.birthDate ||
      eligibilityForm.searchId
    );
  };

  const formatBirthday = (dateOfBirth: Date) => {
    const formattedDate = format(dateOfBirth, 'MM/dd/yyyy');

    setFormValue('birthDate', formattedDate);
  };

  const validateAndFormat = () => {
    const candiDate = new Date(eligibilityForm.birthDate);
    const today = new Date();
    const birthdayLimit = new Date(1900, 1, 1);

    const goodDate =
      isValid(candiDate) &&
      isBefore(candiDate, today) &&
      isAfter(candiDate, birthdayLimit);

    const errMsg = !isValid(candiDate)
      ? t('facility-web.error.date-format')
      : isBefore(candiDate, today) && isAfter(candiDate, birthdayLimit)
        ? undefined
        : t('facility-web.error.date-range');

    setFieldError('date', errMsg);

    if (goodDate) {
      formatBirthday(candiDate);
    }

    return !goodDate;
  };

  const validateSearchId = () => {
    const trimmedSearchId = eligibilityForm.searchId.trim();
    setFormValue('searchId', trimmedSearchId);
    const isValidSearchId = /^\d*$/.test(trimmedSearchId);
    setFieldError(
      'searchId',
      isValidSearchId ? undefined : t('facility-web.error.search-id'),
    );
    return !isValidSearchId;
  };

  const validateZipCode = () => {
    const trimmedZipCode = eligibilityForm.zipCode.trim();
    setFormValue('zipCode', trimmedZipCode);
    const isValidZipCode = /^\d*$/.test(trimmedZipCode);
    setFieldError(
      'zipCode',
      isValidZipCode ? undefined : t('facility-web.error.zip-code'),
    );
    return !isValidZipCode;
  };

  const validateGroupId = () => {
    const trimmedGroupId = eligibilityForm.groupId.trim();
    setFormValue('groupId', trimmedGroupId);
    const isValidGroupId = /^[a-zA-Z0-9]*$/.test(trimmedGroupId);
    setFieldError(
      'groupId',
      isValidGroupId ? undefined : t('facility-web.error.group-id'),
    );
    return !isValidGroupId;
  };

  const onEligibilitySubmit = (event: FormEvent) => {
    if (event.preventDefault) event.preventDefault();
    const eligibilityCheck: MemberBigNInput = { ...eligibilityForm };

    if (hasData()) {
      if (
        validateAndFormat() ||
        validateSearchId() ||
        validateGroupId() ||
        validateZipCode()
      ) {
        return;
      } else {
        setMemberEligibilityError(false);
        fetchMemberByEligiblity(eligibilityCheck);
      }
    } else {
      setMemberEligibilityError(true);
    }
  };

  useEffect(() => {
    if (memberCheckForBigN?.status === 'ineligible') {
      setMemberEligibilityError(true);
    } else if (memberCheckForBigN?.status === 'noMatch') {
      setShowAdditionalFields(true);
      if (eligibilityForm.zipCode) {
        setShowGroupId(true);
        if (eligibilityForm.zipCode && eligibilityForm.groupId) {
          setMemberEligibilityError(true);
        }
      }
    } else if (memberCheckForBigN?.status === 'matchFound') {
      router?.push(MEMBER_INFO_PATH);
    }
    if (!memberCheckForBigN && error) {
      setMemberEligibilityError(true);
    }
  }, [memberCheckForBigN]);

  return (
    <StyledEligibilityLookup>
      <div>
        <div>
          <h6 className="has-rds-mb-16 has-rds-mt-24 is-rds-h6 rds-font-weight-medium">
            Confirm eligibility and generate code
          </h6>
        </div>
        <div className="large-box has-rds-bg-white has-rds-p-24 has-rds-radius-4 has-rds-box-shadow-neutral-spread-6">
          {ENABLE_FACILITY_CODE_CHECK ? (
            <p
              data-testid="generate-code"
              className="is-rds-body-3 has-rds-font-weight-semibold"
            >
              <span>
                {t('facility-web.confirm.eligibility.generate.code.para1')}
                <br />
                {t('facility-web.confirm.eligibility.generate.code.para2')}
              </span>
            </p>
          ) : (
            <p className="is-rds-body-3 has-rds-font-weight-semibold">
              Can only be used for Medicare and Medicaid members. You may be
              prompted to enter the member’s Health Plan Member ID or Group ID
              if we cannot find the member using just the information below.
            </p>
          )}
          <form id="submit-eligibility" onSubmit={onEligibilitySubmit}>
            <div>
              <label htmlFor="first-name" className="is-rds-body-3">
                First Name (Enter exactly as it displays on member’s health plan
                ID card)
              </label>
              <PartnerInput
                type="text"
                data-testid="first-name"
                className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
                id="first-name"
                value={eligibilityForm.firstName}
                onChange={(e) => setFormValue('firstName', e.target.value)}
                onBlur={(e) => setFormValue('firstName', e.target.value.trim())}
              />
              <label htmlFor="last-name" className="is-rds-body-3">
                Last Name (Enter exactly as it displays on member’s health plan
                ID card)
              </label>
              <PartnerInput
                type="text"
                className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
                id="last-name"
                value={eligibilityForm.lastName}
                onChange={(e) => setFormValue('lastName', e.target.value)}
                onBlur={(e) => setFormValue('lastName', e.target.value.trim())}
              />
              <label htmlFor="dob" className="is-rds-body-3">
                Date of Birth
              </label>
              <PartnerInput
                placeholder="MM/DD/YYYY"
                type="text"
                className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
                id="dob"
                data-testid="dob"
                value={eligibilityForm.birthDate}
                onChange={(e) => setFormValue('birthDate', e.target.value)}
                onBlur={() => {
                  // hasErrors();
                  validateAndFormat();
                }}
              />
              {fieldErrors.date && <ErrorInfo message={fieldErrors.date} />}
              <label htmlFor="plan-member-id" className="is-rds-body-3">
                Health Plan Member ID (Don’t include dashes or anything after)
              </label>
              <PartnerInput
                type="text"
                className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
                id="plan-member-id"
                value={eligibilityForm.searchId}
                onChange={(e) => setFormValue('searchId', e.target.value)}
                onBlur={validateSearchId}
              />
              {fieldErrors.searchId && (
                <ErrorInfo message={fieldErrors.searchId} />
              )}
            </div>
            <AdditionalFields
              data-testid="additional-fields"
              className={cx('has-rds-mb-24', {
                hide: !showAdditionalFields,
                show: showAdditionalFields,
              })}
            >
              <label htmlFor="member-zip" className="is-rds-body-3">
                Member 5-digit Zip Code (Enter member’s primary zip code)
              </label>
              <PartnerInput
                data-testid="member-zip"
                className="has-rds-radius-4 has-rds-mt-8"
                type="text"
                id="member-zip"
                value={eligibilityForm.zipCode}
                onChange={(e) => setFormValue('zipCode', e.target.value)}
                onBlur={validateZipCode}
              />
              {fieldErrors.zipCode && (
                <ErrorInfo message={fieldErrors.zipCode} />
              )}
            </AdditionalFields>
            <GroupIdField
              data-testid="groupId-field"
              className={cx('has-rds-mb-24', {
                hide: !showGroupId,
                show: showGroupId,
              })}
            >
              <label htmlFor="group-id" className="is-rds-body-3">
                Group ID
                <SecondaryLabel className="secondary-label is-rds-sm">
                  If Group ID is not found on insured member’s ID card, please
                  try 36000
                </SecondaryLabel>
              </label>
              <PartnerInput
                type="text"
                className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
                id="group-id"
                value={eligibilityForm.groupId}
                onChange={(e) => setFormValue('groupId', e.target.value)}
                onBlur={validateGroupId}
              />
              {fieldErrors.groupId && (
                <ErrorInfo message={fieldErrors.groupId} />
              )}
            </GroupIdField>
            {memberEligibilityError && (
              <ErrorInfo message={t('facility-web.error.member-code')} />
            )}
            <PartnerButton data-testid="submit-eligibility">
              Submit
            </PartnerButton>
          </form>
        </div>
      </div>
    </StyledEligibilityLookup>
  );
}

export default EligibilityLookup;
